import React, { useState, useEffect } from "react";
import { useAuth } from "../../hooks/useAuth";
import { useAlert } from "../../contexts/AlertContext";
import { transactionAPI } from "../../services/api";
import WalletConnectionModal from "../../components/wallet/WalletConnectionModal";
import { hasConnectedWallet } from "../../types";
import {
  MdWarning,
  MdInfo,
  MdRefresh,
  MdAccountBalanceWallet,
} from "react-icons/md";
import GlobalStyles, { CommonClasses } from "../../global/GlobalStyle";

const Withdraw = () => {
  const { user, refreshUser } = useAuth();
  const { addAlert } = useAlert();
  const [formData, setFormData] = useState({
    coin: "bitcoin",
    network: "bitcoin",
    amount: "",
    address: "",
    balanceType: "stake",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [coinPrice, setCoinPrice] = useState<number | null>(null);
  const [isRefreshingPrice, setIsRefreshingPrice] = useState(false);
  const [showWalletModal, setShowWalletModal] = useState(false);

  const WarningIcon = MdWarning as React.FC<React.SVGProps<SVGSVGElement>>;
  const InfoIcon = MdInfo as React.FC<React.SVGProps<SVGSVGElement>>;
  const RefreshIcon = MdRefresh as React.FC<React.SVGProps<SVGSVGElement>>;
  const WalletIcon = MdAccountBalanceWallet as React.FC<
    React.SVGProps<SVGSVGElement>
  >;

  const coins = [
    { id: "bitcoin", name: "Bitcoin", networks: ["bitcoin"], symbol: "BTC" },
    { id: "ethereum", name: "Ethereum", networks: ["erc20"], symbol: "ETH" },
    {
      id: "tether",
      name: "Tether (USDT)",
      networks: ["erc20", "bep20", "trc20"],
      symbol: "USDT",
    },
    { id: "solana", name: "Solana", networks: ["solana"], symbol: "SOL" },
    { id: "ripple", name: "Ripple", networks: ["ripple"], symbol: "XRP" },
    {
      id: "binancecoin",
      name: "BNB",
      networks: ["bep20", "bep2"],
      symbol: "BNB",
    },
    {
      id: "stellar",
      name: "Stellar (XLM)",
      networks: ["stellar"],
      symbol: "XLM",
    },
    // Add Zora and Dogecoin
    { id: "zora", name: "Zora", networks: ["zora", "erc20"], symbol: "ZORA" },
    {
      id: "dogecoin",
      name: "Dogecoin",
      networks: ["dogecoin"],
      symbol: "DOGE",
    },
  ];

  const balanceTypes = [
    {
      id: "ledger",
      name: "Ledger Balance",
      amount: Number(user?.ledger_balance || 0),
      description: "Withdraw to connected wallet",
      requiresWallet: true,
    },
    {
      id: "stake",
      name: "Stake Balance",
      amount: Number(user?.stake_balance || 0),
      description: "Withdraw to external wallet address",
      requiresWallet: false,
    },
    {
      id: "bonus",
      name: "Bonus Balance",
      amount: Number(user?.bonus_balance || 0),
      description: "Withdraw to connected wallet",
      requiresWallet: true,
    },
    {
      id: "earnings",
      name: "Earnings Balance",
      amount: Number(user?.earnings_balance || 0),
      description: "Withdraw to connected wallet",
      requiresWallet: true,
    },
  ];

  const currentCoin = coins.find((coin) => coin.id === formData.coin);
  const selectedBalance = balanceTypes.find(
    (b) => b.id === formData.balanceType,
  );
  const availableBalance = Number(selectedBalance?.amount || 0);
  const walletConnected = hasConnectedWallet(user);

  useEffect(() => {
    if (formData.coin) {
      fetchCoinPrice();
      const coin = coins.find((c) => c.id === formData.coin);
      if (coin) {
        setFormData((prev) => ({ ...prev, network: coin.networks[0] }));
      }
    }
    // eslint-disable-next-line
  }, [formData.coin]);

  const fetchCoinPrice = async () => {
    try {
      setIsRefreshingPrice(true);
      const response = await transactionAPI.getCoinPrice(formData.coin);
      const priceData = response.data;

      if (!priceData.price) {
        throw new Error("Price not found in response");
      }

      setCoinPrice(priceData.price);
    } catch (error: any) {
      console.error("Failed to fetch coin price:", error);
      const fallbackPrices: { [key: string]: number } = {
        bitcoin: 45000,
        ethereum: 2500,
        tether: 1,
        solana: 100,
        ripple: 0.6,
        binancecoin: 350,
        stellar: 0.12,
        // Add Zora and Dogecoin fallback prices
        zora: 0.5,
        dogecoin: 0.08,
      };

      const fallbackPrice = fallbackPrices[formData.coin];
      setCoinPrice(fallbackPrice);

      addAlert({
        type: "warning",
        message: `Using estimated ${formData.coin.toUpperCase()} price: $${fallbackPrice}`,
      });
    } finally {
      setIsRefreshingPrice(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (parseFloat(formData.amount) > availableBalance) {
      addAlert({ type: "error", message: "Insufficient balance" });
      setIsSubmitting(false);
      return;
    }

    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      addAlert({ type: "error", message: "Please enter a valid amount" });
      setIsSubmitting(false);
      return;
    }

    if (!selectedBalance?.requiresWallet && !formData.address.trim()) {
      addAlert({ type: "error", message: "Please enter withdrawal address" });
      setIsSubmitting(false);
      return;
    }

    if (selectedBalance?.requiresWallet && !walletConnected) {
      addAlert({ type: "error", message: "Please connect your wallet first" });
      setIsSubmitting(false);
      return;
    }

    if (!coinPrice) {
      addAlert({
        type: "error",
        message: "Unable to get current price. Please try again.",
      });
      setIsSubmitting(false);
      return;
    }

    try {
      await transactionAPI.withdraw({
        coin: formData.coin,
        network: formData.network,
        amount: parseFloat(formData.amount),
        to_address: selectedBalance?.requiresWallet
          ? "connected_wallet"
          : formData.address,
        balance_type: formData.balanceType as
          | "ledger"
          | "stake"
          | "bonus"
          | "earnings",
      });

      addAlert({ type: "success", message: "Withdrawal request submitted!" });
      setFormData((prev) => ({ ...prev, amount: "", address: "" }));
    } catch (error: any) {
      addAlert({
        type: "error",
        message: error.response?.data?.error || "Failed to submit withdrawal",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const setMaxAmount = () => {
    const maxAmount = availableBalance * 0.99;
    setFormData((prev) => ({ ...prev, amount: maxAmount.toFixed(8) }));
  };

  const handleWalletConnect = async () => {
    await refreshUser();
    addAlert({ type: "success", message: "Wallet connected successfully!" });
  };

  const cryptoAmount =
    coinPrice && formData.amount
      ? (parseFloat(formData.amount) / coinPrice).toFixed(8)
      : "0";

  return (
    <div className={GlobalStyles.container}>
      {/* Header */}
      <div>
        <h1
          className={`${CommonClasses.textXl} font-bold ${CommonClasses.textGrayStrong} mb-2`}
        >
          Withdraw Funds
        </h1>
        <p className={`${CommonClasses.textGray}`}>
          Withdraw funds from your account to external wallets or your connected
          wallet.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Withdrawal Form */}
        <div className={`${GlobalStyles.sectionWrap}`}>
          <h2
            className={`${CommonClasses.textLg} font-semibold ${CommonClasses.textGrayStrong} mb-4`}
          >
            Withdrawal Details
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Balance Type */}
            <div className={GlobalStyles.formGroup}>
              <label className={GlobalStyles.label}>From Balance</label>
              <select
                name="balanceType"
                value={formData.balanceType}
                onChange={handleChange}
                className={GlobalStyles.input}
              >
                {balanceTypes.map((balance) => (
                  <option key={balance.id} value={balance.id}>
                    {balance.name} - ${Number(balance.amount || 0).toFixed(2)}
                  </option>
                ))}
              </select>
              <p
                className={`${CommonClasses.textXs} ${CommonClasses.textGray} mt-1`}
              >
                {selectedBalance?.description}
              </p>
            </div>

            {/* Wallet Connection for Ledger Balance */}
            {selectedBalance?.requiresWallet && (
              <div className={GlobalStyles.formGroup}>
                <label className={GlobalStyles.label}>Wallet Connection</label>
                {!walletConnected ? (
                  <div className="space-y-3">
                    <button
                      type="button"
                      onClick={() => setShowWalletModal(true)}
                      className={`${GlobalStyles.buttonPrimary}`}
                    >
                      <WalletIcon className="w-5 h-5 mr-2" />
                      Connect Wallet for Withdrawal
                    </button>
                    <p
                      className={`${CommonClasses.textXs} ${CommonClasses.textGray} text-center`}
                    >
                      You need to connect a wallet to withdraw from your{" "}
                      {selectedBalance.name.toLowerCase()}
                    </p>
                  </div>
                ) : (
                  <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                    <div className={`${CommonClasses.flexBetween}`}>
                      <div className={`${CommonClasses.flexStart} gap-2`}>
                        <WalletIcon className="w-4 h-4 text-green-600" />
                        <span
                          className={`${CommonClasses.textSm} font-medium text-green-800 dark:text-green-200`}
                        >
                          Wallet Connected
                        </span>
                      </div>
                      <span
                        className={`${CommonClasses.textXs} text-green-600 font-medium`}
                      >
                        {user?.wallets?.filter((w) => w.is_connected).length ||
                          0}{" "}
                        wallet(s)
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Coin Selection */}
            <div className={GlobalStyles.formGroup}>
              <label className={GlobalStyles.label}>Coin</label>
              <select
                name="coin"
                value={formData.coin}
                onChange={handleChange}
                className={GlobalStyles.input}
              >
                {coins.map((coin) => (
                  <option key={coin.id} value={coin.id}>
                    {coin.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Network Selection */}
            <div className={GlobalStyles.formGroup}>
              <label className={GlobalStyles.label}>Network</label>
              <select
                name="network"
                value={formData.network}
                onChange={handleChange}
                className={GlobalStyles.input}
              >
                {currentCoin?.networks.map((network) => (
                  <option key={network} value={network}>
                    {network.toUpperCase()}
                  </option>
                ))}
              </select>
            </div>

            {/* Amount */}
            <div className={GlobalStyles.formGroup}>
              <label className={GlobalStyles.label}>
                Amount (Available: ${availableBalance.toFixed(2)})
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                  $
                </span>
                <input
                  type="number"
                  name="amount"
                  value={formData.amount}
                  onChange={handleChange}
                  placeholder="0.00"
                  step="0.00000001"
                  min="0"
                  max={availableBalance}
                  className={`${GlobalStyles.input} pl-8 pr-20`}
                  required
                />
                <button
                  type="button"
                  onClick={setMaxAmount}
                  className="absolute right-12 top-1/2 transform -translate-y-1/2 text-xs bg-primary text-white px-2 py-1 rounded hover:bg-primary/90 transition-colors"
                >
                  MAX
                </button>
                <button
                  type="button"
                  onClick={fetchCoinPrice}
                  disabled={isRefreshingPrice}
                  className={`${
                    GlobalStyles.buttonIconOutline
                  } absolute right-2 top-1/2 transform -translate-y-1/2 ${
                    isRefreshingPrice ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                >
                  <RefreshIcon
                    className={`w-4 h-4 ${
                      isRefreshingPrice ? "animate-spin" : ""
                    }`}
                  />
                </button>
              </div>

              {/* Real-time Conversion Display */}
              {formData.amount && coinPrice && (
                <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <div className={`${CommonClasses.flexBetween}`}>
                    <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
                      You Will Receive:
                    </span>
                    <span className="text-lg font-bold text-blue-600 dark:text-blue-400">
                      {cryptoAmount} {currentCoin?.symbol}
                    </span>
                  </div>
                  <div className={`${CommonClasses.flexBetween} mt-1`}>
                    <span className="text-xs text-blue-700 dark:text-blue-300">
                      Current Price:
                    </span>
                    <span className="text-xs font-semibold text-blue-600">
                      $
                      {coinPrice.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 6,
                      })}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Withdrawal Address */}
            {!selectedBalance?.requiresWallet && (
              <div className={GlobalStyles.formGroup}>
                <label className={GlobalStyles.label}>Withdrawal Address</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder={`Enter ${formData.coin.toUpperCase()} address`}
                  className={GlobalStyles.input}
                  required={!selectedBalance?.requiresWallet}
                />
              </div>
            )}

            {/* Fee Information */}
            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <div className={`${CommonClasses.flexStart} gap-3`}>
                <InfoIcon className="w-5 h-5 text-blue-500 mt-0.5" />
                <div>
                  <p
                    className={`${CommonClasses.textSm} font-medium text-blue-800 dark:text-blue-200`}
                  >
                    Withdrawal Fee: 1%
                  </p>
                  <p
                    className={`${CommonClasses.textXs} text-blue-700 dark:text-blue-300 mt-1`}
                  >
                    Network fees may apply. Minimum withdrawal: $10.00
                    {formData.amount && (
                      <span>
                        {" "}
                        • Fee: $
                        {(parseFloat(formData.amount) * 0.01).toFixed(2)}
                      </span>
                    )}
                  </p>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={
                isSubmitting ||
                !formData.amount ||
                (selectedBalance?.requiresWallet && !walletConnected) ||
                (!selectedBalance?.requiresWallet && !formData.address)
              }
              className={`${GlobalStyles.buttonPrimary} ${
                isSubmitting ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              {isSubmitting ? "Processing..." : "Withdraw Funds"}
            </button>
          </form>
        </div>

        {/* Balance Summary & Information */}
        <div className="space-y-6">
          {/* Balance Summary */}
          <div className={`${GlobalStyles.sectionWrap}`}>
            <h2
              className={`${CommonClasses.textLg} font-semibold ${CommonClasses.textGrayStrong} mb-4`}
            >
              Balance Summary
            </h2>
            <div className="space-y-3">
              {balanceTypes.map((balance) => (
                <div
                  key={balance.id}
                  className={`${
                    CommonClasses.flexBetween
                  } p-3 border rounded-lg transition-colors ${
                    formData.balanceType === balance.id
                      ? "bg-purple-50 border-purple-200 dark:bg-purple-900/20 dark:border-purple-800"
                      : "border-gray-200 dark:border-gray-700"
                  }`}
                >
                  <div>
                    <span
                      className={`block ${CommonClasses.textSm} font-medium ${CommonClasses.textGrayStrong}`}
                    >
                      {balance.name}
                    </span>
                    <span
                      className={`${CommonClasses.textXs} ${CommonClasses.textGray}`}
                    >
                      {balance.description}
                    </span>
                  </div>
                  <span className="font-bold text-primary dark:text-purple-400">
                    ${Number(balance.amount || 0).toFixed(2)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Important Notes */}
          <div className={`${GlobalStyles.sectionWrap}`}>
            <h2
              className={`${CommonClasses.textLg} font-semibold ${CommonClasses.textGrayStrong} mb-4`}
            >
              Important Information
            </h2>
            <div className="space-y-3">
              {[
                "Withdrawals are processed within 24 hours after approval",
                "Ensure the address supports the selected network",
                "Double-check all details before submitting",
                "Ledger, Bonus, and Earnings balance withdrawals go to your connected wallet",
                "Stake balance withdrawals can go to any external wallet address",
              ].map((note, index) => (
                <div key={index} className={`${CommonClasses.flexStart} gap-3`}>
                  <WarningIcon className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                  <p
                    className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}
                  >
                    {note}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Wallet Connection Modal */}
      {showWalletModal && (
        <WalletConnectionModal
          isOpen={showWalletModal}
          onClose={() => setShowWalletModal(false)}
          onConnect={handleWalletConnect}
        />
      )}
    </div>
  );
};

export default Withdraw;
